import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { User, Heart, MessageCircle, Share2, Settings, Plus, Image as ImageIcon, FileText, Video, BarChart3, Mic } from 'lucide-react';

const CreatorPreview = ({ creatorData, onEdit, onComplete }) => {
  const {
    username = 'username',
    displayName = 'Your Display Name',
    bio = 'Your bio will appear here...',
    profilePicture = null,
  } = creatorData;

  return (
    <PreviewContainer>
      <PreviewHeader>
        <Logo>
          <LogoIcon>☕</LogoIcon>
          <LogoText>Trentyway</LogoText>
        </Logo>
        <HeaderActions>
          <ActionButton><User size={16} /> Your Page</ActionButton>
          <ActionButton><Heart size={16} /></ActionButton>
          <ActionButton><MessageCircle size={16} /></ActionButton>
          <ActionButton><Share2 size={16} /></ActionButton>
          <ActionButton><Settings size={16} /></ActionButton>
        </HeaderActions>
      </PreviewHeader>

      <MainContent>
        <ProfileSection>
          <ProfileImage $src={profilePicture}>
            {!profilePicture && <User size={48} color="#000" />}
          </ProfileImage>
          <ProfileInfo>
            <ProfileName>{displayName}</ProfileName>
            <ProfileUrl>bucx.io/{username}</ProfileUrl>
          </ProfileInfo>
        </ProfileSection>

        <PageActions>
          <ActionGroup>
            <PageActionButton><ImageIcon size={16} /> Cover</PageActionButton>
            <PageActionButton><Settings size={16} /></PageActionButton>
            <PageActionButton><Plus size={16} /></PageActionButton>
          </ActionGroup>
          <CreateButton><Plus size={16} /> Create</CreateButton>
        </PageActions>

        <ContentTabs>
          <Tab $active>About</Tab>
          <Tab>Gallery</Tab>
          <Tab>Posts</Tab>
        </ContentTabs>

        <NextStepBanner>
          <BannerIcon>📋</BannerIcon>
          <BannerText>Next Step: Connect a payment method to accept payments on Trentyway.</BannerText>
        </NextStepBanner>

        <ContentGrid>
          <LeftColumn>
            <ContentSection>
              <SectionHeader><SectionTitle>Write a quick update</SectionTitle></SectionHeader>
              <UpdateInput placeholder="Write a quick update..." />
              <UpdateActions>
                <UpdateActionButton><ImageIcon size={16} /> Image</UpdateActionButton>
                <UpdateActionButton><FileText size={16} /> Blog Post</UpdateActionButton>
                <UpdateActionButton><Video size={16} /> Video</UpdateActionButton>
                <UpdateActionButton><BarChart3 size={16} /> Poll</UpdateActionButton>
                <UpdateActionButton><Mic size={16} /> Audio</UpdateActionButton>
              </UpdateActions>
            </ContentSection>

            <ContentSection>
              <SectionHeader>
                <SectionTitle>About</SectionTitle>
                <EditButton onClick={onEdit}><Settings size={16} /></EditButton>
              </SectionHeader>
              <SectionContent>{bio}</SectionContent>
            </ContentSection>

            <ContentSection>
              <SectionHeader><SectionTitle>Set a goal</SectionTitle><AddButton><Plus size={16} /> Add</AddButton></SectionHeader>
            </ContentSection>
          </LeftColumn>

          <RightColumn>
            <ContentSection>
              <SectionHeader><SectionTitle>Page intro</SectionTitle><AddButton><Plus size={16} /> Add</AddButton></SectionHeader>
            </ContentSection>
            <ContentSection>
              <SectionHeader><SectionTitle>Intro video</SectionTitle><AddButton><Plus size={16} /> Add</AddButton></SectionHeader>
            </ContentSection>
          </RightColumn>
        </ContentGrid>
      </MainContent>

      <Footer>
        <FooterLinks>
          <FooterLink>Cookie Settings</FooterLink>
          <FooterLink>Terms</FooterLink>
          <FooterLink>Privacy</FooterLink>
        </FooterLinks>
      </Footer>

      <CompletionActions>
        <EditProfileButton onClick={onEdit}>Edit Profile</EditProfileButton>
        <CompleteSetupButton onClick={onComplete}>Complete Setup</CompleteSetupButton>
      </CompletionActions>
    </PreviewContainer>
  );
};

const PreviewContainer = styled.div`min-height: 100vh; background: #000000; color: #ffffff;`;
const PreviewHeader = styled.div`display: flex; justify-content: space-between; padding: 1rem 2rem; border-bottom: 1px solid #333333; background: #111111;`;
const Logo = styled.div`display: flex; align-items: center; gap: 0.5rem;`;
const LogoIcon = styled.span`font-size: 1.5rem; color: #e0c16f;`;
const LogoText = styled.h1`font-size: 1.5rem; color: #e0c16f; margin: 0;`;
const HeaderActions = styled.div`display: flex; gap: 0.5rem;`;
const ActionButton = styled.button`display: flex; gap: 0.5rem; padding: 0.5rem 1rem; background: #1a1a1a; color: #cccccc; border: 1px solid #333333; border-radius: 8px; cursor: pointer; &:hover { border-color: #e0c16f; }`;
const MainContent = styled.div`padding: 2rem; max-width: 1200px; margin: 0 auto;`;
const ProfileSection = styled.div`display: flex; align-items: center; margin-bottom: 2rem;`;
const ProfileImage = styled.div`width: 80px; height: 80px; border-radius: 50%; background: ${p => p.$src ? `url(${p.$src}) center/cover` : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'}; display: flex; align-items: center; justify-content: center; margin-right: 1.5rem; border: 3px solid #e0c16f;`;
const ProfileInfo = styled.div`flex: 1;`;
const ProfileName = styled.h2`font-size: 2rem; color: #ffffff; margin: 0 0 0.5rem 0;`;
const ProfileUrl = styled.p`color: #e0c16f; margin: 0;`;
const PageActions = styled.div`display: flex; justify-content: space-between; margin-bottom: 2rem;`;
const ActionGroup = styled.div`display: flex; gap: 0.5rem;`;
const PageActionButton = styled.button`display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem 1rem; background: #1a1a1a; color: #cccccc; border: 1px solid #333333; border-radius: 20px; cursor: pointer;`;
const CreateButton = styled.button`display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.5rem; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;`;
const ContentTabs = styled.div`display: flex; border-bottom: 1px solid #333333; margin-bottom: 2rem;`;
const Tab = styled.button`padding: 1rem 2rem; background: transparent; color: ${p => p.$active ? '#e0c16f' : '#888'}; border: none; border-bottom: 3px solid ${p => p.$active ? '#e0c16f' : 'transparent'}; font-size: 1rem; cursor: pointer;`;
const NextStepBanner = styled.div`display: flex; align-items: center; gap: 1rem; padding: 1rem 1.5rem; background: rgba(224, 193, 111, 0.1); border: 1px solid #e0c16f; color: #e0c16f; border-radius: 8px; margin-bottom: 2rem;`;
const BannerIcon = styled.span`font-size: 1.2rem;`;
const BannerText = styled.p`margin: 0;`;
const ContentGrid = styled.div`display: grid; grid-template-columns: 2fr 1fr; gap: 2rem;`;
const LeftColumn = styled.div`display: flex; flex-direction: column; gap: 2rem;`;
const RightColumn = styled.div`display: flex; flex-direction: column; gap: 2rem;`;
const ContentSection = styled.div`background: #111111; border: 1px solid #333333; border-radius: 12px; padding: 1.5rem;`;
const SectionHeader = styled.div`display: flex; justify-content: space-between; margin-bottom: 1rem;`;
const SectionTitle = styled.h3`color: #ffffff; margin: 0;`;
const EditButton = styled.button`background: none; color: #888; border: none; cursor: pointer;`;
const AddButton = styled.button`display: flex; align-items: center; gap: 0.25rem; background: #333333; color: #fff; border: none; padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer;`;
const SectionContent = styled.p`color: #cccccc; line-height: 1.5; margin: 0;`;
const UpdateInput = styled.textarea`width: 100%; padding: 1rem; border: 1px solid #333333; border-radius: 8px; background: #1a1a1a; color: #fff; resize: none; min-height: 80px; margin-bottom: 1rem; &:focus { outline: none; border-color: #e0c16f; }`;
const UpdateActions = styled.div`display: flex; gap: 0.5rem; flex-wrap: wrap;`;
const UpdateActionButton = styled.button`display: flex; align-items: center; gap: 0.5rem; background: #1a1a1a; color: #ccc; border: 1px solid #333333; padding: 0.5rem 1rem; border-radius: 20px; cursor: pointer; &:hover { border-color: #e0c16f; color: #e0c16f; }`;
const Footer = styled.div`padding: 2rem; border-top: 1px solid #333333; background: #111111; margin-top: 4rem;`;
const FooterLinks = styled.div`display: flex; gap: 2rem; justify-content: center;`;
const FooterLink = styled.a`color: #888; cursor: pointer; &:hover { color: #e0c16f; }`;
const CompletionActions = styled.div`position: fixed; bottom: 2rem; right: 2rem; display: flex; gap: 1rem; z-index: 1000;`;
const EditProfileButton = styled(motion.button)`padding: 1rem 2rem; background: #1a1a1a; color: #fff; border: 2px solid #333333; border-radius: 12px; cursor: pointer;`;
const CompleteSetupButton = styled(motion.button)`padding: 1rem 2rem; background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%); color: #000; border: none; border-radius: 12px; font-weight: 600; cursor: pointer;`;

export default CreatorPreview;